import { applyCalendarDates } from "./dates";
import {
  canonicalizeOrgName,
  collapseEducation,
  isAcademicRole,
  isJunkPosition,
  isLikelySchool,
  isRealExperience,
  looksLikePlaceholder,
  matchKnownSchool,
  preferCollegeEducation,
  schoolIdentityKey,
  toHtmlSummary,
  extractDutyLines,
  type DraftProject,
  type ResumeDraft,
} from "./draft";

const EMAIL_RE = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i;
const PHONE_RE = /(?:\+?\d{1,3}[\s.-]?)?(?:\(?\d{2,4}\)?[\s.-]?)?\d{3,4}[\s.-]?\d{3,4}/;
const URL_RE = /https?:\/\/[^\s)]+/i;
const SKILLS_RE =
  /\b(?:skills?\s*[:\-]\s*|the tools i use most are\s+|tools i (?:actually )?use\s*[:\-]\s*)([^\n.]+)/i;
const DURATION_RE = /\b(?:for\s+)?(\d+\s*\+?\s*(?:years?|months?))\b/i;
const YEAR_RE = /\b((?:19|20)\d{2}(?:\s*(?:-|to|–)\s*(?:(?:19|20)\d{2}|now|present))?)\b/i;
const BUILT_RE = /\bbuilt\s+(?:a |an |the )?([^.,;]{6,90})/i;
const ALSO_BUILT_RE = /\bi also built\s+(?:a |an |the )?([^.,;]{6,90})/i;

const FIELD_ALIASES: Record<string, string> = {
  cs: "Computer Science",
  compsci: "Computer Science",
  "comp sci": "Computer Science",
  "computer science": "Computer Science",
  ee: "Electrical Engineering",
  "electrical engineering": "Electrical Engineering",
  me: "Mechanical Engineering",
  mba: "MBA",
  "human computer interaction": "Human-Computer Interaction",
  hci: "Human-Computer Interaction",
};

const TITLE_ALIASES: Array<[RegExp, string]> = [
  [/software\s+developer\s+engineer/i, "Software Engineer"],
  [/software\s+dev(?:eloper)?\s+eng(?:ineer)?/i, "Software Engineer"],
  [/sde\b/i, "Software Engineer"],
  [/full[\s-]?stack developer/i, "Full-Stack Developer"],
  [/front[\s-]?end engineer/i, "Frontend Engineer"],
  [/back[\s-]?end engineer/i, "Backend Engineer"],
];

export type KnownIdentity = {
  name?: string;
  email?: string;
  location?: string;
};

export const looksLikeDate = (value: string): boolean => {
  const text = value.trim();
  if (!text || text.length > 36) return false;
  if (/\b(19|20)\d{2}\b/.test(text)) return true;
  if (/\b(present|current|now|today)\b/i.test(text)) return true;
  if (/\d+\s*\+?\s*(?:years?|months?)\b/i.test(text)) return true;
  if (/^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i.test(text)) return true;
  return false;
};

const titleCase = (value: string): string =>
  value
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const normalizeField = (value: string): string => {
  const trimmed = value.replace(/\s+/g, " ").trim();
  if (!trimmed) return "";
  return FIELD_ALIASES[trimmed.toLowerCase()] ?? titleCase(trimmed);
};

const normalizeTitle = (value: string): string => {
  const stripped = value
    .replace(
      /^(?:i currently work as|i work as|i was|i am|i'm|i have been|i've been|i worked as|currently work as|worked as|work as|have worked as|then|after)\s+(?:a |an )?/i,
      "",
    )
    .replace(/^(?:a |an )\s+/i, "")
    .replace(/\s+/g, " ")
    .trim();
  if (isJunkPosition(stripped)) return "";
  for (const [pattern, alias] of TITLE_ALIASES) {
    if (pattern.test(stripped)) return alias;
  }
  return titleCase(stripped);
};

const takeDate = (value: string): { rest: string; date: string } => {
  let rest = value.replace(/\s+/g, " ").trim();
  const year = rest.match(YEAR_RE);
  if (year?.[1]) {
    return {
      rest: rest.replace(year[0], " ").replace(/\s+/g, " ").trim(),
      date: year[1].replace(/\s+/g, " "),
    };
  }
  const duration = rest.match(DURATION_RE);
  if (duration?.[1]) {
    return {
      rest: rest.replace(duration[0], " ").replace(/\s+/g, " ").trim(),
      date: duration[1].replace(/\s+/g, " "),
    };
  }
  return { rest, date: "" };
};

const takeArea = (value: string): { rest: string; area: string } => {
  const match = value.match(/\b(?:for|in)\s+([A-Za-z][A-Za-z0-9 +.#/-]{1,40})$/i);
  if (!match?.[1]) return { rest: value.trim(), area: "" };
  return {
    rest: value.slice(0, match.index).replace(/\s+/g, " ").trim(),
    area: normalizeField(match[1]),
  };
};

const splitLocation = (company: string, location = ""): { company: string; location: string } => {
  let nextCompany = company.trim().replace(/[.,;]+$/g, "");
  let nextLocation = location.trim().replace(/[.,;]+$/g, "");

  const inMatch = nextCompany.match(/^(.*?)\s+in\s+([A-Z][A-Za-z .'-]{1,40})$/);
  if (inMatch && !nextLocation) {
    nextCompany = inMatch[1].trim();
    nextLocation = inMatch[2].trim();
  }
  if (looksLikePlaceholder(nextLocation)) nextLocation = "";

  return { company: canonicalizeOrgName(nextCompany), location: nextLocation };
};

const looksLikeName = (line: string): boolean => {
  if (line.length > 60 || line.includes("@") || /\d{4}/.test(line)) return false;
  const words = line.split(/\s+/).filter(Boolean);
  return (
    words.length >= 2 && words.length <= 4 && words.every((word) => /^[\p{L}.'-]+$/u.test(word))
  );
};

const inferStudyType = (date: string, institution: string, area: string): string => {
  if (!area) return "";
  if (/\b(mba|master|m\.?s|m\.?sc|ph\.?d|doctorate|doctoral|dphil|associate)\b/i.test(`${area} ${date}`)) {
    return "";
  }
  if (!isLikelySchool(institution) || /\b(community college|city college)\b/i.test(institution)) {
    return "";
  }
  if (/\b(4 years|four years)\b/i.test(date) || !date || /(?:19|20)\d{2}/.test(date)) return "Bachelor's";
  return "";
};

const parseEducationBlob = (
  blob: string,
): { institution: string; date: string; area: string; studyType: string } | null => {
  let rest = blob.replace(/,/g, " ").replace(/\s+/g, " ").trim();
  let area = "";
  let studyType = "";

  const degree = rest.match(
    /\b(Bachelor'?s|Master'?s|Doctorate|Doctoral|MBA|Ph\.?D\.?|DPhil|Ed\.?D\.?|M\.?Eng\.?|M\.?Sc\.?|M\.?S\.?|M\.?A\.?|B\.?Sc\.?|B\.?S\.?|B\.?A\.?)/i,
  );
  if (degree?.[1]) {
    const token = degree[1];
    studyType = /doctorate|doctoral|ph\.?d|dphil|ed\.?d/i.test(token)
      ? "PhD"
      : /mba/i.test(token)
        ? "MBA"
        : /m\.?s|m\.?a|m\.?eng|master/i.test(token)
          ? "Master's"
          : "Bachelor's";
    rest = rest.replace(degree[0], " ").replace(/\s+/g, " ").trim();
  }

  const dateTaken = takeDate(rest);
  rest = dateTaken.rest;

  const fromMatch = rest.match(/\b(?:at|from)\s+(.+)$/i);
  let fieldPart = "";
  let institutionPart = rest;
  if (fromMatch) {
    fieldPart = rest.slice(0, fromMatch.index).replace(/\s+/g, " ").trim();
    institutionPart = fromMatch[1].trim();
  }

  const areaTaken = takeArea(fieldPart || rest);
  if (areaTaken.area) {
    area = areaTaken.area;
    if (!fromMatch) rest = areaTaken.rest;
  } else if (fieldPart) {
    area = normalizeField(fieldPart.replace(/^(?:in|for)\s+/i, ""));
  }

  const known = matchKnownSchool(institutionPart) ?? matchKnownSchool(blob);
  const institution =
    known ?? canonicalizeOrgName(institutionPart.replace(/^(?:at|from)\s+/i, ""));
  if (!institution || institution.length < 2) return null;

  if (!area) {
    const leftover = (fromMatch ? fieldPart : rest)
      .replace(new RegExp(`\\b${institution.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "i"), " ")
      .replace(new RegExp(`\\b${schoolIdentityKey(institution).replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "i"), " ")
      .replace(/\b(?:at|from|in|for)\b/gi, " ")
      .replace(/[.]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    if (leftover) area = normalizeField(leftover);
  }

  return {
    institution,
    date: dateTaken.date,
    area,
    studyType: studyType || inferStudyType(dateTaken.date, institution, area),
  };
};

const DUTY_PREFIX =
  /^(?:i own|i built|i led|i run|i ran|i shipped|my main responsibilities are|main responsibilities included|i work on|most of my time goes to)\s+/i;
const ACTION_START =
  /^(built|reduced|shipped|owned|own|cut|cutting|developed|led|lead|created|designed|wrote|ran|run|supported|on-call|handled|improved)/i;
const LEAD_DUTY =
  /^(?:i own|i run|i work on|most of my time goes to|my main responsibilities are|main responsibilities included)\s+/i;

const dutiesFromParagraph = (para: string): string[] => {
  const sentences = para
    .split(/(?<=[.!?])\s+/)
    .map((item) => item.trim())
    .filter(Boolean);
  return sentences.slice(1).flatMap((sentence) => {
    const lead = LEAD_DUTY.test(sentence);
    const built = /^i built\s+/i.test(sentence);
    let cleaned = sentence.replace(DUTY_PREFIX, "").replace(/[.]+$/, "").trim();
    if (cleaned.length < 12 || looksLikePlaceholder(cleaned)) return [];
    if (/^(location|skills|email|phone|i hold|i studied|i live|i also built)\b/i.test(cleaned)) {
      return [];
    }
    if (lead) {
      cleaned = `Lead ${cleaned.charAt(0).toLowerCase()}${cleaned.slice(1)}`;
    } else if (built) {
      cleaned = `Built ${cleaned.charAt(0).toLowerCase()}${cleaned.slice(1)}`;
    } else if (!ACTION_START.test(cleaned)) {
      cleaned = `Worked on ${cleaned.charAt(0).toLowerCase()}${cleaned.slice(1)}`;
    } else {
      cleaned = `${cleaned.charAt(0).toUpperCase()}${cleaned.slice(1)}`;
    }
    return extractDutyLines(cleaned);
  });
};

const attachStatedDuties = (draft: ResumeDraft, text: string) => {
  const paragraphs = text
    .split(/\n+/)
    .map((item) => item.trim())
    .filter(Boolean);
  for (const item of draft.experience ?? []) {
    const company = item.company?.trim();
    if (!company || item.summary) continue;
    const pattern = new RegExp(`\\bat\\s+${company.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "i");
    const para = paragraphs.find((line) => pattern.test(line));
    if (!para) continue;
    const duties = dutiesFromParagraph(para);
    if (duties.length > 0) item.summary = toHtmlSummary(duties.join("\n"));
  }
};

export const parseNotesToDraft = (
  text: string,
  known: KnownIdentity = {},
  now: Date = new Date(),
): ResumeDraft => {
  const draft: ResumeDraft = {
    basics: {},
    experience: [],
    education: [],
    skills: [],
    projects: [],
  };

  const trimmed = text.trim();
  if (known.name) draft.basics!.name = known.name;
  if (known.email) draft.basics!.email = known.email;
  if (known.location?.trim()) draft.basics!.location = known.location.trim();

  if (!trimmed) return draft;

  const emailMatch = trimmed.match(EMAIL_RE);
  if (emailMatch) draft.basics!.email = emailMatch[0];

  const urlMatch = trimmed.match(URL_RE);
  if (urlMatch) draft.basics!.url = urlMatch[0];

  const phoneMatch = trimmed.match(PHONE_RE);
  if (phoneMatch && phoneMatch[0].replace(/\D/g, "").length >= 10 && !looksLikePlaceholder(phoneMatch[0])) {
    draft.basics!.phone = phoneMatch[0].trim();
  }

  const BASED_RE = /\b(?:based in|located in|live in|location\s*[:\-]\s*)([^.,;\n]{2,48})/i;
  const based = trimmed.match(BASED_RE);
  if (based?.[1] && !draft.basics!.location && !looksLikePlaceholder(based[1])) {
    draft.basics!.location = based[1].trim().replace(/\s+/g, " ");
  }

  const lines = trimmed
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (!draft.basics!.name && looksLikeName(lines[0] ?? "")) {
    draft.basics!.name = lines[0];
  }

  let working = trimmed;

  const skillMatch = working.match(SKILLS_RE);
  if (skillMatch?.[1]) {
    const keywords = skillMatch[1]
      .split(/[,;/|]/)
      .map((item) => item.trim())
      .filter(
        (item) =>
          item.length > 1 && item.length < 40 && !looksLikePlaceholder(item) && !/[\[\]]/.test(item),
      );
    if (keywords.length > 0) {
      draft.skills = [{ name: "", keywords, level: 0 }];
    }
    working = working.replace(SKILLS_RE, " ");
  }

  const built =
    working.match(ALSO_BUILT_RE) ??
    working.split(/\n+/).reduce<RegExpMatchArray | null>((found, para) => {
      if (found || /\bat\s+/i.test(para) || ALSO_BUILT_RE.test(para)) return found;
      return para.match(BUILT_RE);
    }, null);
  if (built?.[1] && !looksLikePlaceholder(built[1])) {
    const description = built[1].trim();
    const project: DraftProject = {
      name: titleCase(description.replace(/\s+in\s+[A-Za-z+#.]{2,24}$/i, "")),
      summary: `Built ${description}.`,
      keywords: [],
    };
    const stack = description.match(/\bin\s+([A-Za-z][A-Za-z0-9+#./ ]{1,40})$/i);
    if (stack?.[1]) {
      project.keywords = stack[1]
        .split(/[,/&]/)
        .map((item) => item.trim())
        .filter(Boolean);
    }
    draft.projects = [project];
  }

  const STUDY_RE =
    /(?:after|before)?\s*(?:stud(?:y|ied|ying)|graduated(?:\s+from)?|hold(?:s)?(?:\s+a)?)\s+(?:degree\s+)?(?:at\s+|from\s+)?([^.,;]+)/gi;
  for (const match of working.matchAll(STUDY_RE)) {
    const parsed = parseEducationBlob(match[1] ?? "");
    if (!parsed || looksLikePlaceholder(parsed.institution)) continue;
    if (looksLikePlaceholder(parsed.area)) parsed.area = "";
    draft.education!.push(parsed);
  }
  working = working.replace(STUDY_RE, " ");

  const NAMED_SCHOOL_RE =
    /\b([A-Z][^.\n,]{1,70}?(?:college|university|school|institute|academy|polytechnic))\s*(?:,|\s+in)?\s*((?:19|20)\d{2})?/gi;
  for (const match of working.matchAll(NAMED_SCHOOL_RE)) {
    const institution = canonicalizeOrgName(match[1] ?? "");
    if (!institution) continue;
    if (
      (draft.education ?? []).some(
        (item) => schoolIdentityKey(item.institution ?? "") === schoolIdentityKey(institution),
      )
    ) {
      continue;
    }
    draft.education!.push({
      institution,
      date: match[2] ?? "",
      area: "",
      studyType: "",
    });
  }
  working = working.replace(NAMED_SCHOOL_RE, " ");

  const knownSchool = matchKnownSchool(working);
  if (
    knownSchool &&
    !(draft.education ?? []).some(
      (item) => schoolIdentityKey(item.institution ?? "") === schoolIdentityKey(knownSchool),
    )
  ) {
    const token = schoolIdentityKey(knownSchool);
    const window = working.match(
      new RegExp(`\\b${token.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b([^\\n]{0,100})`, "i"),
    );
    const parsed = parseEducationBlob(`${knownSchool}${window?.[1] ?? ""}`);
    if (parsed) draft.education!.push(parsed);
  }

  const graduated = trimmed.match(/\bgraduated\s+(?:in\s+)?((?:19|20)\d{2})\b/i);
  if (graduated?.[1]) {
    const missingDate = (draft.education ?? []).filter((item) => !item.date?.trim());
    if (missingDate.length === 1) {
      draft.education = (draft.education ?? []).map((item) =>
        item.date?.trim() ? item : { ...item, date: graduated[1] },
      );
    }
  }

  const experience: Array<{
    company: string;
    position: string;
    location: string;
    date: string;
  }> = [];

  const WORK_RE =
    /\b(?:i\s+)?(?:currently\s+)?(?:have\s+)?(?:work(?:ed)?(?:\s+as)?|was|am|'m)\s+(?:a |an )?([^.,;]+?)\s+at\s+([^.,;]+)/gi;
  for (const match of working.matchAll(WORK_RE)) {
    const position = normalizeTitle(match[1] ?? "");
    const dateTaken = takeDate(match[2] ?? "");
    const split = splitLocation(dateTaken.rest);
    if (!position || !split.company) continue;
    if (isLikelySchool(split.company) && !isAcademicRole(position)) {
      draft.education!.push({
        institution: split.company,
        date: dateTaken.date,
        area: "",
        studyType: inferStudyType(dateTaken.date, split.company, ""),
      });
      continue;
    }
    if (!isRealExperience(split.company)) continue;
    experience.push({
      company: split.company,
      position,
      location: split.location,
      date: dateTaken.date,
    });
  }
  working = working.replace(WORK_RE, " ");

  const AT_RE = /\b([A-Za-z][^.,;]{1,48}?)\s+at\s+([^.,;]+)/gi;
  for (const match of working.matchAll(AT_RE)) {
    const position = normalizeTitle(match[1] ?? "");
    const dateTaken = takeDate(match[2] ?? "");
    const split = splitLocation(dateTaken.rest);
    if (!position || !split.company) continue;
    if (isLikelySchool(split.company) && !isAcademicRole(position)) {
      draft.education!.push({
        institution: split.company,
        date: dateTaken.date,
        area: "",
        studyType: inferStudyType(dateTaken.date, split.company, ""),
      });
      continue;
    }
    if (!isRealExperience(split.company)) continue;
    experience.push({
      company: split.company,
      position,
      location: split.location,
      date: dateTaken.date,
    });
  }
  working = working.replace(AT_RE, " ");

  const THEN_RE = /\bthen(?:\s+a|\s+an)?\s+([^.,;]+)/gi;
  const thenRoles = [...working.matchAll(THEN_RE)]
    .map((match) => {
      const blob = match[1] ?? "";
      const atMatch = blob.match(/^(.*?)\s+at\s+(.+)$/i);
      if (atMatch) {
        const position = normalizeTitle(atMatch[1] ?? "");
        const dateTaken = takeDate(atMatch[2] ?? "");
        const split = splitLocation(dateTaken.rest);
        return {
          position,
          company: split.company,
          location: split.location,
          date: dateTaken.date,
        };
      }
      return {
        position: normalizeTitle(blob),
        company: "",
        location: "",
        date: "",
      };
    })
    .filter((item) => item.position);

  const primary = experience[0];
  const thenExperience = thenRoles.map((item) => ({
    company: item.company || primary?.company || "",
    position: item.position,
    location: item.location || primary?.location || "",
    date: item.date,
  }));

  const mergedExperience = [...thenExperience, ...experience].filter(
    (item) => item.company && isRealExperience(item.company) && !isJunkPosition(item.position),
  );

  draft.experience = mergedExperience.filter(
    (item, index, list) =>
      list.findIndex(
        (other) => other.company === item.company && other.position === item.position,
      ) === index,
  );

  attachStatedDuties(draft, trimmed);

  draft.education = collapseEducation(
    preferCollegeEducation((draft.education ?? []).filter((item) => item.institution?.trim())),
  );

  if (!draft.basics!.headline && draft.experience[0]?.position) {
    draft.basics!.headline = draft.experience[0].position;
  }
  if (!draft.basics!.location && draft.experience[0]?.location) {
    draft.basics!.location = draft.experience[0].location;
  }
  if (known.location?.trim()) {
    draft.basics!.location = known.location.trim();
  }

  return applyCalendarDates(draft, now);
};

export const parsePlainTextToDraft = parseNotesToDraft;
